/**
 * **Rent from a vendor** — `docs/visit-page.md` §10.2, Board 6 B (sheet 560):
 * *What* (required) and *Type* (optional); *Vendor* (required) — the picked
 * vendor as a selected row over a map of the yard, the vendor and the site,
 * and *9 min from the yard · 18 min on to the site*; *Days*; *How it gets
 * there*; *Picks up* + *At* (*at the site about 7:30*); *Takes it back* +
 * *By* (*Keystone closes at 5:00*, amber when *By* is after it); *Rate*
 * (internal, *a day · est. $95.00*) and *Confirmation*; *Note for the crew*.
 * Footer *The pickup and the return go on Jesse's Friday in the crew app.* ·
 * **Cancel** · **Book**.
 *
 * It opens from **Book it** (a rental the quote planned — its line filled in),
 * from **Rent from a vendor** (empty), from **Change**, and from the
 * Overview's Ready row (Slice 1 mounts it there with `tab: 'overview'`).
 * Every figure under a field is the server's (`POST …/rentals/preview`); the
 * page adds nothing up.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  zonedLocal, type IsoMoment, type RentalHow, type RentalOptions, type RentalPreview, type VisitRead, type VisitTabKey,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { useVisitWrite, visitPath, type VisitWriteResult } from './api.js'
import { COMMON, RENT, RENTALS } from './copy.js'
import { dayText, localTime, money, timeIn } from './format.js'
import { Face } from './face.js'
import { VisitGlyph } from './glyphs.js'
import { DayChips, OneOfChips } from './day-chips.js'
import { boxClass, MomentField, PickField, SheetField, TextBox } from './equipment-fields.js'
import { FIELD_INPUT, RadioDot } from './fields.js'
import { RentMap } from './rent-map.js'
import { VisitButton, VisitCard, VisitTile } from './sheet.js'

interface Draft {
  description: string
  typeId: string | null
  vendorId: string | null
  dayIndexes: number[]
  how: RentalHow
  pickupUserId: string | null
  pickupAt: IsoMoment | null
  returnUserId: string | null
  returnBy: IsoMoment | null
  rate: string
  confirmation: string
  crewNote: string
}

function draftFrom(options: RentalOptions): Draft {
  const d = options.draft
  return {
    description: d.description,
    typeId: d.typeId ?? null,
    vendorId: d.vendorId || null,
    dayIndexes: d.dayIndexes,
    how: d.how,
    pickupUserId: d.pickupUserId ?? null,
    pickupAt: d.pickupAt ?? null,
    returnUserId: d.returnUserId ?? null,
    returnBy: d.returnBy ?? null,
    rate: options.internal?.rate ?? '',
    confirmation: d.confirmation,
    crewNote: d.crewNote,
  }
}

/** What goes to the server — the preview, a booking, a change. */
function body(draft: Draft, internal: boolean) {
  return {
    description: draft.description.trim(),
    typeId: draft.typeId,
    ...(draft.vendorId ? { vendorId: draft.vendorId } : {}),
    dayIndexes: draft.dayIndexes,
    how: draft.how,
    pickupUserId: draft.pickupUserId,
    pickupAt: draft.pickupAt,
    returnUserId: draft.returnUserId,
    returnBy: draft.returnBy,
    ...(internal && draft.rate.trim() ? { rate: draft.rate.trim() } : {}),
    confirmation: draft.confirmation.trim(),
    crewNote: draft.crewNote.trim(),
  }
}

/**
 * What the preview reads — the vendor, the days, the times and the rate —
 * and only what is there: a sheet half typed (*What* cleared, no day chosen)
 * still asks, and is never refused for a field the preview does not read.
 */
function previewBody(draft: Draft, internal: boolean) {
  return {
    ...(draft.vendorId ? { vendorId: draft.vendorId } : {}),
    ...(draft.dayIndexes.length > 0 ? { dayIndexes: draft.dayIndexes } : {}),
    how: draft.how,
    ...(draft.pickupAt ? { pickupAt: draft.pickupAt } : {}),
    ...(draft.returnBy ? { returnBy: draft.returnBy } : {}),
    ...(internal && /^\d+(\.\d{1,2})?$/.test(draft.rate.trim()) ? { rate: draft.rate.trim() } : {}),
  }
}

export interface RentSheetProps {
  visitId: string
  read: VisitRead
  /** The rental to book or change; none for **Rent from a vendor** from nothing. */
  rentalId?: string | null
  /** Which tab's read the write answers with — `equipment` on the tab, `overview` from the Ready row. */
  tab?: VisitTabKey
  phone: boolean
  onClose: () => void
  onWritten?: (result: VisitWriteResult) => void
}

export function RentSheet({ visitId, read, rentalId = null, tab = 'equipment', phone, onClose, onWritten }: RentSheetProps) {
  const write = useVisitWrite(visitId)
  const [options, setOptions] = useState<RentalOptions | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [preview, setPreview] = useState<RentalPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const zone = read.timeZone

  /* The options — again whenever the vendor changes, for its row, its drive and the map. */
  const load = useCallback((vendorId: string | null) => {
    const params = new URLSearchParams()
    if (rentalId) params.set('rentalId', rentalId)
    if (vendorId) params.set('vendorId', vendorId)
    const query = params.toString()
    return request<RentalOptions>(`${visitPath(visitId, '/rentals/options')}${query ? `?${query}` : ''}`)
  }, [visitId, rentalId])

  useEffect(() => {
    let live = true
    load(null)
      .then((got) => { if (live) { setOptions(got); setDraft(draftFrom(got)) } })
      .catch((cause: unknown) => { if (live) setError(cause instanceof Error ? cause.message : String(cause)) })
    return () => { live = false }
  }, [load])

  const update = (patch: Partial<Draft>) => setDraft((was) => (was ? { ...was, ...patch } : was))

  function pickVendor(vendorId: string) {
    update({ vendorId })
    load(vendorId).then(setOptions).catch((cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)))
  }

  /* What depends on what is typed — *closes at*, *at the site about*, the estimate — asked of the server. */
  const asked = useRef(0)
  const internal = Boolean(options?.internal)
  const previewKey = draft ? JSON.stringify(previewBody(draft, internal)) : ''
  useEffect(() => {
    if (!draft) return undefined
    const ticket = ++asked.current
    const timer = window.setTimeout(() => {
      request<RentalPreview>(visitPath(visitId, '/rentals/preview'), { method: 'POST', body: previewBody(draft, internal) })
        .then((got) => { if (ticket === asked.current) setPreview(got) })
        .catch(() => { /* a preview that fails leaves the last one; the booking says what is wrong */ })
    }, 200)
    return () => window.clearTimeout(timer)
    // `previewKey` is the draft as sent.
  }, [previewKey, visitId])

  const ready = Boolean(draft && draft.description.trim() && draft.vendorId && draft.dayIndexes.length > 0) && !busy
  const existing = options?.rental?.version ?? null
  const booking = !options?.rental || options.rental.state === 'to_book'

  async function book() {
    if (!draft || !ready) return
    setBusy(true)
    setError(null)
    try {
      const result = rentalId && existing !== null
        ? await write('PATCH', `/rentals/${rentalId}`, { ...body(draft, internal), version: existing }, { tab })
        : await write('POST', '/rentals', body(draft, internal), { tab })
      onWritten?.(result)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const people = useMemo(() => (options?.people ?? []).map((person) => ({
    value: person.id, label: person.name, lead: <Face person={person} size={22} />,
  })), [options])
  const firstDay = options?.days.find((day) => draft?.dayIndexes.includes(day.index)) ?? options?.days[0]
  const vendor = options?.vendors.find((one) => one.vendor.id === draft?.vendorId) ?? null
  const picker = options?.people.find((person) => person.id === draft?.pickupUserId) ?? null

  /*
   * The footer's sentence is true only while the crew app shows rental stops
   * (`crew.rental_stops`, §27.4) — and only for a pickup with a person and a
   * time; otherwise the footer carries the buttons alone.
   */
  const note = read.capabilities['crew.rental_stops'] && draft?.how === 'pickup' && picker && draft.pickupAt
    ? RENT.footer(picker.firstName, dayText(zonedLocal(new Date(draft.pickupAt), zone).date, 'weekday'))
    : undefined

  const size = phone ? 'phone' : 'card'
  return (
    <VisitCard
      width={562}
      phone={phone}
      asDrawn="vp-06 B"
      title={RENT.title}
      sub={options?.planned
        ? RENT.sub(read.reference, options.planned.quoteReference, timeIn(zone, options.planned.at, 'dateAmpm'))
        : read.reference}
      mark={<VisitTile glyph="truck" tone="amber" size={36} />}
      onClose={onClose}
      {...(note ? { note } : {})}
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{RENT.cancel}</VisitButton>
          <VisitButton size={size} tone="primary" glyph={booking ? 'check' : undefined} disabled={!ready} onClick={() => void book()}>
            {booking ? RENT.book : COMMON.save}
          </VisitButton>
        </>
      )}
    >
      {draft && options && (
        <div className="flex flex-col gap-[13px]">
          <div className={cn('grid gap-x-3.5 gap-y-3', phone ? 'grid-cols-1' : 'grid-cols-[1.5fr_1fr]')}>
            <SheetField label={RENT.what} required>
              {({ id, describedBy }) => (
                <TextBox id={id} describedBy={describedBy} phone={phone} value={draft.description} strong
                  onChange={(description) => update({ description })} />
              )}
            </SheetField>
            <SheetField label={RENT.type} optional={RENT.optional}>
              {({ id, describedBy }) => (
                <PickField id={id} describedBy={describedBy} label={RENT.type} phone={phone} clearable value={draft.typeId}
                  onChange={(typeId) => update({ typeId })}
                  options={options.types.map((type) => ({ value: type.id, label: type.name }))} />
              )}
            </SheetField>
          </div>

          <SheetField label={RENT.vendor} required group>
            {({ labelId }) => (
              <div role="radiogroup" aria-labelledby={labelId} className="flex flex-col gap-2">
                {options.vendors.map((row) => {
                  const on = row.vendor.id === draft.vendorId
                  const place = row.vendor.line1 && row.vendor.city ? RENTALS.address(row.vendor.line1, row.vendor.city) : row.vendor.line1 ?? row.vendor.city
                  const contact = place && row.vendor.phone ? COMMON.dot(place, row.vendor.phone) : place ?? row.vendor.phone
                  return (
                    <div key={row.vendor.id}
                      className={cn('overflow-hidden rounded-card',
                        on ? 'border-[1.5px] border-accent ring-[3px] ring-accent-ring' : 'border border-line')}>
                      <button type="button" role="radio" aria-checked={on} onClick={() => { if (!on) pickVendor(row.vendor.id) }}
                        className={cn('flex w-full items-center gap-2.5 px-3 py-2.5 text-left', phone && 'min-h-[52px]')}>
                        <RadioDot on={on} size={16} />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <b className="text-[12.5px]">{row.vendor.name}</b>
                          {contact && <span className="text-[11.5px] text-text-muted">{contact}</span>}
                          {row.vendor.hours && <span className="text-[11.5px] text-text-muted">{row.vendor.hours}</span>}
                        </span>
                        {row.rentedThisYear > 0 && (
                          <span className="shrink-0 text-right text-[11px] leading-[1.3] text-text-muted">
                            {RENT.rentedHere}<br />{RENT.timesThisYear(String(row.rentedThisYear))}
                          </span>
                        )}
                      </button>
                      {on && options.map && (
                        <div className="border-t border-line-faint">
                          <RentMap places={{ ...options.map, labels: { ...options.map.labels, vendor: row.vendor.shortName } }} />
                        </div>
                      )}
                    </div>
                  )
                })}
                {vendor?.drive && (
                  <span className="-mt-1 text-[11px] leading-[1.4] text-text-faint">
                    {RENT.drive(String(vendor.drive.fromYard), String(vendor.drive.toSite))}
                  </span>
                )}
              </div>
            )}
          </SheetField>

          <SheetField label={RENT.days} required group>
            {({ labelId }) => (
              <DayChips labelledBy={labelId} phone={phone} value={draft.dayIndexes}
                onChange={(dayIndexes) => update({ dayIndexes })}
                days={options.days.map((day) => ({ index: day.index, label: RENT.day(String(day.index), dayText(day.date, 'day')) }))} />
            )}
          </SheetField>

          <SheetField label={RENT.howItGetsThere} group>
            {({ labelId }) => (
              <OneOfChips<RentalHow> labelledBy={labelId} phone={phone} value={draft.how} onChange={(how) => update({ how })}
                options={[{ value: 'pickup', label: RENT.how.pickup }, { value: 'delivery', label: RENT.how.delivery }]} />
            )}
          </SheetField>

          <div className={cn('grid gap-x-3.5 gap-y-3', phone ? 'grid-cols-1' : 'grid-cols-2')}>
            <SheetField label={RENT.picksUp}>
              {({ id, describedBy }) => (
                <PickField id={id} describedBy={describedBy} label={RENT.picksUp} phone={phone} clearable
                  value={draft.pickupUserId} options={people} onChange={(pickupUserId) => update({ pickupUserId })} />
              )}
            </SheetField>
            <SheetField label={RENT.at} hint={preview?.atSite ? RENT.atSite(localTime(preview.atSite)) : undefined}>
              {({ id, describedBy }) => (
                <MomentField id={id} describedBy={describedBy} label={RENT.at} phone={phone} timeZone={zone}
                  fallbackDay={firstDay?.date ?? read.now.slice(0, 10)} value={draft.pickupAt}
                  onChange={(pickupAt) => update({ pickupAt })} />
              )}
            </SheetField>
          </div>

          <div className={cn('grid gap-x-3.5 gap-y-3', phone ? 'grid-cols-1' : 'grid-cols-2')}>
            <SheetField label={RENT.takesItBack}>
              {({ id, describedBy }) => (
                <PickField id={id} describedBy={describedBy} label={RENT.takesItBack} phone={phone} clearable
                  value={draft.returnUserId} options={people} onChange={(returnUserId) => update({ returnUserId })} />
              )}
            </SheetField>
            <SheetField
              label={RENT.by}
              hint={preview?.closesAt && vendor ? (
                <span className={cn(preview.afterClose && 'text-warn')}>
                  {preview.afterClose
                    ? COMMON.dot(RENT.closesAt(vendor.vendor.shortName, localTime(preview.closesAt)), RENT.afterClose)
                    : RENT.closesAt(vendor.vendor.shortName, localTime(preview.closesAt))}
                </span>
              ) : undefined}
            >
              {({ id, describedBy }) => (
                <MomentField id={id} describedBy={describedBy} label={RENT.by} phone={phone} timeZone={zone}
                  fallbackDay={firstDay?.date ?? read.now.slice(0, 10)} value={draft.returnBy}
                  onChange={(returnBy) => update({ returnBy })} />
              )}
            </SheetField>
          </div>

          <div className={cn('grid gap-x-3.5 gap-y-3', phone ? 'grid-cols-1' : 'grid-cols-2')}>
            {options.internal && (
              <SheetField label={RENT.rate}>
                {({ id, describedBy }) => (
                  <span className={boxClass(phone, 'gap-2 !bg-surface-internal')}>
                    <span aria-hidden className="inline-flex" style={{ color: 'rgb(var(--status-kind-fee-fg))' }}>
                      <VisitGlyph name="lock" size={12} stroke={2.4} />
                    </span>
                    <span className="flex min-w-0 flex-1 items-center">
                      <span aria-hidden>{RENT.dollar}</span>
                      <input id={id} aria-describedby={describedBy} inputMode="decimal" value={draft.rate}
                        onChange={(event) => update({ rate: event.target.value })} className={cn(FIELD_INPUT, 'h-full')} />
                    </span>
                    {preview?.internal && (
                      <span className="shrink-0 whitespace-nowrap text-[12px] text-text-faint">
                        {RENT.rateSub(money(preview.internal.estimate))}
                      </span>
                    )}
                  </span>
                )}
              </SheetField>
            )}
            <SheetField label={RENT.confirmation} optional={RENT.optional}>
              {({ id, describedBy }) => (
                <TextBox id={id} describedBy={describedBy} phone={phone} value={draft.confirmation}
                  onChange={(confirmation) => update({ confirmation })} />
              )}
            </SheetField>
          </div>

          <SheetField label={RENT.note} optional={RENT.optional}>
            {({ id, describedBy }) => (
              <TextBox id={id} describedBy={describedBy} phone={phone} value={draft.crewNote}
                onChange={(crewNote) => update({ crewNote })} />
            )}
          </SheetField>

          {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
        </div>
      )}
      {!draft && error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
    </VisitCard>
  )
}
